import { EV_OPERATION_MODEL_LABELS } from "@station/contracts";
import { Alert, EmptyState, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { EvCharger } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { formatKw } from "../../../../features/ev/ev-values.js";
import { useEvChargers } from "../../../../features/ev/queries.js";
import {
  ConnectorStatusBadge,
  CredentialBadge,
  evSearchShape,
  EvStationPage,
  type EvStationContext,
  OnlineBadge,
  TableFrame,
  Th,
} from "../../../../features/ev/shared.js";
import { errorMessage } from "../../../../lib/errors.js";
import { formatDateTime } from "../../../../lib/format.js";

const searchSchema = z.object(evSearchShape);

export const Route = createFileRoute("/_app/sarj/uniteler/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ChargersRoute,
});

/**
 * Şarj üniteleri (Prompt 19 web, `GET /ev/chargers?includeUnmanaged=true`): OCPP bağlantısı (bağlı mı, son görülme),
 * üretici / model / yazılım (BootNotification), kimlik bilgisi durumu, işletme modeli ve konnektörlerin son durumu.
 * Ünitenin kendisi (ad, OCPP kimliği, konnektörler) istasyon varlıklarındadır; kimlik bilgisi, komutlar ve uzaktan başlatma
 * ayrıntı sayfasındadır.
 */
function ChargersRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <EvStationPage title={t("menu.evChargers")} description={t("ev.chargers.description")}>
      {(context) => <ChargersBody context={context} />}
    </EvStationPage>
  );
}

function ChargersBody({ context }: { context: EvStationContext }): ReactElement {
  const { t } = useTranslation();
  const chargers = useEvChargers(context.station.id);
  if (chargers.error) return <Alert tone="danger">{errorMessage(chargers.error)}</Alert>;
  if (!chargers.data) return <SkeletonRows rows={5} />;
  if (chargers.data.length === 0)
    return (
      <EmptyState
        headingLevel={2}
        icon={<EvCharger />}
        title={t("ev.chargers.emptyTitle")}
        description={t("ev.chargers.emptyDescription")}
      />
    );
  return (
    <TableFrame label={t("ev.chargers.caption")}>
      <thead className="bg-muted/60 text-xs uppercase text-muted-foreground">
        <tr>
          <Th>{t("ev.fields.charger")}</Th>
          <Th>{t("ev.chargers.connection")}</Th>
          <Th>{t("ev.chargers.device")}</Th>
          <Th>{t("ev.chargers.credentialLabel")}</Th>
          <Th>{t("ev.chargers.model")}</Th>
          <Th>{t("ev.chargers.connectors")}</Th>
        </tr>
      </thead>
      <tbody>
        {chargers.data.map((charger) => (
          <tr key={charger.id} className="border-t align-top">
            <th scope="row" className="px-3 py-2 text-left font-medium">
              <span className="grid gap-0.5">
                <Link
                  to="/sarj/uniteler/$id"
                  params={{ id: charger.id }}
                  search={{ station: context.station.id } as never}
                  className="underline underline-offset-4"
                >
                  {charger.name}
                </Link>
                <span className="font-mono text-xs font-normal text-muted-foreground">
                  {charger.ocppIdentity ?? t("ev.chargers.noIdentity")}
                </span>
                <span className="text-xs font-normal text-muted-foreground">
                  {t("ev.chargers.maxPower", { power: formatKw(charger.maxPowerKw) })}
                  {charger.status !== "ACTIVE" ? ` · ${t(`enums.assetStatus.${charger.status}`)}` : ""}
                </span>
              </span>
            </th>
            <td className="px-3 py-2">
              <span className="grid justify-items-start gap-1">
                {charger.managed ? (
                  <OnlineBadge online={charger.ocpp.connected} />
                ) : (
                  <StatusBadge tone="neutral">{t("ev.chargers.notManaged")}</StatusBadge>
                )}
                <span className="text-xs text-muted-foreground">
                  {t("ev.chargers.lastSeen", { at: formatDateTime(charger.ocpp.lastSeenAt) })}
                </span>
              </span>
            </td>
            <td className="px-3 py-2 text-xs">
              <span className="grid gap-0.5">
                <span className="text-sm">
                  {[charger.ocpp.vendor ?? charger.brand, charger.ocpp.model ?? charger.assetModel]
                    .filter(Boolean)
                    .join(" ") || "—"}
                </span>
                {charger.ocpp.firmwareVersion ? (
                  <span className="text-muted-foreground">
                    {t("ev.chargers.firmware", { version: charger.ocpp.firmwareVersion })}
                  </span>
                ) : null}
                {charger.ocpp.serialNo ? (
                  <span className="font-mono text-muted-foreground">{charger.ocpp.serialNo}</span>
                ) : null}
              </span>
            </td>
            <td className="px-3 py-2">
              <span className="grid justify-items-start gap-1">
                <CredentialBadge charger={charger} />
                {charger.credential.rotatedAt ? (
                  <span className="text-xs text-muted-foreground">
                    {t("ev.chargers.rotatedAt", { at: formatDateTime(charger.credential.rotatedAt) })}
                  </span>
                ) : null}
              </span>
            </td>
            <td className="px-3 py-2 text-xs">{EV_OPERATION_MODEL_LABELS[charger.operationModel]}</td>
            <td className="px-3 py-2">
              <ul className="grid gap-1" aria-label={t("ev.chargers.connectorsOf", { name: charger.name })}>
                {charger.connectors.map((connector) => (
                  <li key={connector.connectorNo} className="flex flex-wrap items-center gap-1.5 text-xs">
                    <span className="whitespace-nowrap">
                      {t("ev.connector.no", { no: connector.connectorNo })} ·{" "}
                      {t(`enums.connectorType.${connector.connectorType}`)} · {formatKw(connector.powerKw)}
                    </span>
                    <ConnectorStatusBadge status={connector.ocppStatus} errorCode={connector.errorCode} />
                  </li>
                ))}
              </ul>
            </td>
          </tr>
        ))}
      </tbody>
    </TableFrame>
  );
}
